import React, { ReactNode, useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { AxiosResponse } from "axios";
import { useTypedSelector } from "store";
import { useDispatch } from "react-redux";
import { getProductValueAttributes } from "store/reducers/products/parameters/parameters.actions";
import { MacroResponse } from "types/misc";
import { ProcessState } from "types/process";
import { BuildMacroResponseResult } from "store/reducers/products/macros/macros.utils";
import { Alert, Stack } from "@mui/material";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import { ActionButtonConfirmStatus } from "components";

interface MacroButtonProps {
  performApiCall: (string) => Promise<AxiosResponse<MacroResponse>>;
  apiCallDependencies: ReadonlyArray<any>;
  buttonLabel: string;
  dialogTitle: string;
  tooltipText: string;
  processingMessage: string;
  completionMessage: string;
  timeOutMessage: string;
  informationContent: ReactNode;
  timeOutFailureDescription: string;
  pvasUpdated: boolean;
  recommendValidation: boolean;
}

export default function MacroButton({
  performApiCall,
  apiCallDependencies,
  buttonLabel,
  dialogTitle,
  tooltipText,
  processingMessage,
  completionMessage,
  timeOutMessage,
  informationContent,
  timeOutFailureDescription,
  pvasUpdated,
  recommendValidation,
}: MacroButtonProps) {
  const dispatch = useDispatch();

  const [processState, setProcessState] = useState<ProcessState>(
    ProcessState.IDLE
  );
  const [statusMessage, setStatusMessage] = useState<string>("");
  const [macroResponse, setMacroResponse] = useState<MacroResponse | null>(
    null
  );

  const productLineId = useTypedSelector(
    (state) => state.products.selectedProductLine?.id
  );

  const resetDialogState = useCallback(() => {
    setProcessState(ProcessState.IDLE);
    setStatusMessage("");
    setMacroResponse(null);
  }, []);

  const handleRun = useCallback(async () => {
    if (!productLineId) {
      setStatusMessage("No product line selected.");
      setProcessState(ProcessState.ERROR);
      return;
    }

    try {
      setStatusMessage(processingMessage);
      setProcessState(ProcessState.PROCESSING);

      const response: AxiosResponse<MacroResponse> = await performApiCall(
        productLineId
      );

      if (response?.data) {
        setMacroResponse(response.data);

        // If PVAs were updated, refresh the PVA list after a successful run.
        if (pvasUpdated) {
          dispatch(getProductValueAttributes());
        }

        setStatusMessage(completionMessage);
        setProcessState(ProcessState.COMPLETE);
      } else {
        setMacroResponse(null);
        setStatusMessage("Request returned no data.");
        setProcessState(ProcessState.ERROR);
      }
    } catch (err: any) {
      // Match existing behavior: special-case timeouts, otherwise use best available error message.
      if (err?.isAxiosError && err?.code === "ECONNABORTED") {
        setMacroResponse({
          failures: [timeOutFailureDescription],
          log: [],
          summaryMessage:
            "To avoid further issues, please wait 15 minutes before performing this action again.",
        });

        setStatusMessage(timeOutMessage);
        setProcessState(ProcessState.ERROR);
        return;
      }

      const msg =
        err?.response?.data?.message ??
        err?.message ??
        "Unknown error occurred.";

      setMacroResponse({
        failures: [`Process failed: ${msg}`],
        log: [],
        summaryMessage: "Could not complete request.",
      });

      setStatusMessage(`Process failed: ${msg}`);
      setProcessState(ProcessState.ERROR);
    }
  }, [...apiCallDependencies, productLineId, useDispatch]);

  // Build the content section for the dialog
  const contentSection = useMemo<ReactNode>(() => {
    return (
      <MacroContentStack>
        {informationContent}

        <Alert variant="outlined" severity="warning" sx={{ mt: 1 }}>
          <strong>Backup your product line before running this!!!!</strong> This
          action cannot be undone.
        </Alert>
      </MacroContentStack>
    );
  }, [informationContent]);

  const resultSection = useMemo<ReactNode>(
    () => BuildMacroResponseResult(macroResponse, recommendValidation),
    [macroResponse, recommendValidation]
  );

  return (
    <ActionButtonConfirmStatus
      // Button Props
      variant="outlined"
      color="secondary"
      // Icon Props
      icon={BuildCircleIcon}
      iconOnly={false}
      // Tooltip Props
      tooltipText={tooltipText}
      tooltipArrow={true}
      tooltipPlacement="right"
      // Dialog Props
      maxWidth="lg"
      dialogTitle={dialogTitle}
      onDialogOpen={resetDialogState}
      disableEscapeKeyDown={true}
      disableCloseOnBackdropClick={true}
      // Content Section Props
      contentSection={contentSection}
      hideContentOnProcessComplete={true}
      // Action Button Props
      defaultButton="CANCEL"
      cancelButtonText="Cancel"
      okButtonText="OK"
      okButtonWarningColor
      onOKButtonClick={handleRun}
      // Status Section Props
      processState={processState}
      statusMessage={statusMessage}
      // Result Section Props
      resultSection={resultSection}
    >
      {buttonLabel}
    </ActionButtonConfirmStatus>
  );
}

export const MacroContentStack = styled(Stack)`
  gap: 16px;
`;

export const MacroBulletList = styled.ul`
  list-style-type: disc;
  list-style-position: outside;
  padding-left: 1.5rem;
  margin: 0;
`;

export const MacroBulletItem = styled.li`
  margin: 8px 0;
  font-size: 1rem;
  line-height: 1.5;
`;
